/**
 * Clean Color Preset
 * Текущие цвета системы как базовый чистый пресет
 */

import {ColorPreset} from '../chartColorPresets';
import { FONTS } from '../fonts';

export const CLEAN_PRESET: ColorPreset = {
    id: 'default',
    name: 'Default Theme',
    description: 'Стандартная цветовая схема',

    elements: {
        lagna: '#dc2626',
        hora: '#2563eb',
        chandra: '#16a34a',
        planetDefault: '#374151'
    },

    degreeLines: {
        mainStroke: '#b848fb',
        divisionStroke: 'rgba(34,99,24,0.22)',
        majorDivision: 'rgba(0,29,159,0.44)',
        mainWidth: 1.0,
        divisionWidth: 0.8,
        majorDivisionWidth: 1.0
    },

    muhurtaDivisions: {
        // Display mode
        renderMode: 'sectors',            // Use colored sectors instead of division lines

        // Division lines styling (when renderMode = 'divisions')
        centralLineStroke: '#ff6600',     // Orange for central line
        centralLineWidth: 5,
        majorDivisionStroke: '#9e81bf',   // Dark for major divisions (every 12°)
        minorDivisionStroke: '#f4bebe',   // Lighter for minor divisions (every 6°)
        majorDivisionWidth: 1,
        minorDivisionWidth: 0.9,
        majorDivisionLength: 200,
        minorDivisionLength: 200,

        // Sector styling (when renderMode = 'sectors') - 30 spectrum colors for muhurta chakra
        sectorFillColors: [
            '#ff0000',  // Sector 1 - Red
            '#ff1a00',  // Sector 2 - Red-orange
            '#ff3300',  // Sector 3 - Red-orange
            '#ff4d00',  // Sector 4 - Orange-red
            '#ff6600',  // Sector 5 - Orange
            '#ff8000',  // Sector 6 - Orange
            '#ff9900',  // Sector 7 - Orange-yellow
            '#ffb300',  // Sector 8 - Yellow-orange
            '#ffcc00',  // Sector 9 - Yellow-orange
            '#ffe600',  // Sector 10 - Yellow
            '#ffff00',  // Sector 11 - Pure yellow
            '#ccff00',  // Sector 12 - Yellow-green
            '#99ff00',  // Sector 13 - Yellow-green
            '#66ff00',  // Sector 14 - Lime green
            '#33ff00',  // Sector 15 - Green-yellow
            '#00ff00',  // Sector 16 - Pure green
            '#00ff33',  // Sector 17 - Green
            '#00ff66',  // Sector 18 - Green-cyan
            '#00ff99',  // Sector 19 - Green-cyan
            '#00ffcc',  // Sector 20 - Cyan-green
            '#00ffff',  // Sector 21 - Pure cyan
            '#00ccff',  // Sector 22 - Cyan-blue
            '#0099ff',  // Sector 23 - Sky blue
            '#0066ff',  // Sector 24 - Blue
            '#0033ff',  // Sector 25 - Blue
            '#0000ff',  // Sector 26 - Pure blue
            '#3300ff',  // Sector 27 - Blue-violet
            '#6600ff',  // Sector 28 - Violet
            '#9900ff',  // Sector 29 - Purple
            '#cc00ff'   // Sector 30 - Magenta
        ],
        sectorStroke: '#999999',          // Gray sector borders
        sectorStrokeWidth: 0.2,           // Thin borders
        sectorFillOpacity: 0.2,           // Sector fill opacity
        sectorStrokeOpacity: 0.9,         // Sector stroke opacity (subtle)
        minorDivisionOpacity: 0.2,        // Minor lines opacity (bright for rainbow effect)

        // Global 360° muhurta chakra intervals
        minorInterval: 6,                 // Minor divisions every 6° (60 total)
        majorInterval: 12,                // Major divisions every 12° (30 total)
        totalSectors: 30,                 // 30 muhurta sectors (each 12°)

        // Sector text styling
        sectorNumberStyle: {
            fontSize: 12,                   // Font size for sector numbers (1-30)
            fontFamily: FONTS.chart.primary, // Font family
            fill: 'rgb(44,57,80)',               // Dark blue-gray for good contrast
            stroke: '#ffffff',              // White stroke for readability
            strokeWidth: 0,               // Thin stroke
            fontWeight: 'normal'               // Bold text
        },

        rulerCodeStyle: {
            fontSize: 12,                   // Font size for ruler codes (Sy, Ch, Ma, etc.)
            fontFamily: FONTS.chart.primary, // Font family
            fill: 'rgb(44,57,80)',               // Darker blue-gray for contrast
            stroke: '#ffffff',              // White stroke for readability
            strokeWidth: 0,               // Thinner stroke
            fontWeight: 'normal'            // Normal weight
        }
    },


    muhurtaLabels: {
        // Muhurta position label styling (e.g., "Ml" for chakra longitude)
        fontSize: 11,                     // Font size for position labels
        fontFamily: FONTS.chart.primary,  // Font family
        fill: '#ffffff',                  // White text for contrast on orange background
        stroke: '#000000',                // Black stroke for text readability
        strokeWidth: 0,                 // Thin stroke for text
        fontWeight: 'normal',               // Bold for visibility
        // Line from center to label
        lineStroke: '#ff6600',            // Orange line to match muhurta theme
        lineWidth: 1,                     // Line width
        // Background square
        backgroundFill: '#ff6600',        // Orange background square
        backgroundStroke: '#ff4602',      // Darker orange border
        backgroundStrokeWidth: 0.1,         // Border width
        backgroundSize: 20,                // Size of the square background (20x20px)
        labelOffset: 90
    },

    labelStyles: {
        default: {
            text: {
                fontSize: 12,
                fontFamily: FONTS.chart.primary,
                fontWeight: 'bold',
                fill: '#333333',
                stroke: '#ffffff',
                strokeWidth: 0.3
            },
            line: {
                stroke: '#999999',
                strokeWidth: 1,
                opacity: 0.8
            },
            background: {
                shape: 'square' as const,
                size: 20,
                fill: '#e0e0e0',
                stroke: '#999999',
                strokeWidth: 1,
                opacity: 0.9
            },
            labelOffset: 90
        },
        planet: {
            text: {
                fontSize: 12,
                fontFamily: FONTS.chart.primary,
                fontWeight: 'bold',
                fill: '#2c5aa0',
                stroke: '#ffffff',
                strokeWidth: 0.5
            },
            line: {
                stroke: '#2c5aa0',
                strokeWidth: 1.5,
                opacity: 0.9
            },
            background: {
                shape: 'circle' as const,
                size: 22,
                fill: '#e3f2fd',
                stroke: '#2c5aa0',
                strokeWidth: 1.5,
                opacity: 0.95
            },
            labelOffset: 95
        },
        square: {
            text: {
                fontSize: 11,
                fontFamily: FONTS.chart.primary,
                fontWeight: 'bold',
                fill: '#ffffff',
                stroke: 'none',
                strokeWidth: 0
            },
            line: {
                stroke: '#60a5fa',
                strokeWidth: 1,
                opacity: 1
            },
            background: {
                shape: 'square' as const,
                size: 20,
                fill: '#60a5fa',
                stroke: '#ffd1f8',
                strokeWidth: 0.5,
                opacity: 1
            },
            labelOffset: 90
        },
        squareOrange: {
            text: {
                fontSize: 11,
                fontFamily: FONTS.chart.primary,
                fontWeight: 'bold',
                fill: '#ffffff',
                stroke: 'none',
                strokeWidth: 0
            },
            line: {
                stroke: '#f97316',
                strokeWidth: 1,
                opacity: 1
            },
            background: {
                shape: 'square' as const,
                size: 20,
                fill: '#f97316',
                stroke: '#ea580c',
                strokeWidth: 0.5,
                opacity: 1
            },
            labelOffset: 90
        }
    },

    detailsPanel: {
        background: '#f8f9fa',
        sectionBackground: 'rgba(21,30,46,0.18)',
        borderDefault: '#b5b5b5',
        borderSection: '#dfdfe4',
        borderRow: '#f1f3f4',
        planetBorderDefault: '#e5e5e5',
        planetBorderHover: '#b848fb',
        planetBorderActive: '#4a90e2',
        planetFocusOutline: '#4a90e2',
        textPrimary: '#333',
        textSecondary: '#666',
        textAccent: '#3e9393',
        loadingSpinner: '#3e9393',
        loadingBackground: '#e9ecef'
    },

    interactive: {
        hoverBoxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
        focusOutlineOffset: '-1px',
        focusVisibleOutlineWidth: '2px',
        planetBorderDefault: '#e5e5e5',
        planetBorderHover: '#b848fb',
        planetBorderActive: '#4a90e2',
        planetFocusOutline: '#4a90e2',
        headerHoverColor: '#4a90e2',
        headerActiveColor: '#4a90e2',
        headerHoverBackground: 'rgba(74, 144, 226, 0.1)',
        sectionHeaderHoverBackground: '#e9ecef'
    },

    chartElements: {
        planetColor: '#2c5aa0',
        yogaStroke: '#333333',
        yogaTextFill: '#000000',
        textFill: '#333333',
        yogaColors: ['#e3f2fd', '#bbdefb', '#90caf9', '#64b5f6', '#42a5f5']
    },

    houseNames: {
        fontSize: 14,
        fontFamily: FONTS.chart.primary,
        fontWeight: 'normal',
        fill: '#666666',
        opacity: 0.7,
        symbolsMode: {
            fontSize: 20,
            fontFamily: FONTS.chart.symbols,
            fontWeight: 'normal',
            dy: 2
        }
    },

    planetSprites: {
        radius: 17,
        strokeWidth: 1,
        fillColor: '#ffffff',
        connectionLineLength: 22,
        dragHighlightColor: '#FFD700',
        dragOtherPlanetsColor: '#9B59B6',
        modes: {
            symbols: { fontSize: 22, fontWeight: 600, fontFamily: FONTS.chart.symbols, dy: 2 },
            short: { fontSize: 16, fontWeight: 600 },
            full: { fontSize: 10, fontWeight: 600 }
        }
    }
};